import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "@/constants/branding";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass shadow-[0_8px_30px_rgba(15,23,42,0.06)]"
          : "bg-white/70 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl 2xl:max-w-[1520px] mx-auto px-6 lg:px-8 flex items-center justify-between h-20 gap-6">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt="Christ Love Breed Church logo"
            className="h-12 w-12 object-contain rounded-full ring-2 ring-gold/40 group-hover:ring-gold transition-all"
          />
          <div className="leading-tight block xl:hidden 2xl:block">
            <div className="font-serif text-lg xl:text-xl text-royal font-semibold whitespace-nowrap">
              Christ Love Breed
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold-500 font-semibold">
              Church · CLBC
            </div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5">
          {NAV_LINKS.slice(0, 10).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap px-2.5 py-2 text-[13px] font-medium rounded-full transition-colors ${
                  isActive
                    ? "text-royal bg-royal/5"
                    : "text-slate-600 hover:text-royal"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/prayer"
            data-testid="nav-cta-prayer"
            className="btn-ghost whitespace-nowrap hidden 2xl:inline-flex"
          >
            Prayer Request
          </Link>
          <Link
            to="/give"
            data-testid="nav-cta-give"
            className="btn-primary text-sm py-2.5 px-6"
          >
            <Heart className="w-4 h-4" /> Give
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="xl:hidden md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-slate-200 text-royal"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-desktop-more"
          className="hidden md:inline-flex xl:hidden items-center justify-center w-11 h-11 rounded-full border border-slate-200 text-royal"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="xl:hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl"
        >
          <div className="container-x py-4 grid grid-cols-2 gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl text-sm font-medium ${
                    isActive
                      ? "bg-royal/5 text-royal"
                      : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/give"
              className="btn-primary col-span-2 mt-3 text-sm"
              data-testid="nav-mobile-give"
            >
              <Heart className="w-4 h-4" /> Give Online
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
